import React, { useEffect, useRef, useState } from "react";
import { useModalContext } from "components/modal";
import { theme } from "styles/theme";
import { dollarFormatter } from "utils";
import { ProductionAssigneeSelect } from "./components/ProductionAssigneeSelect";
import { useTranslation } from "react-i18next";
import { TTargetProduct } from "types/product";
import { TTargetType } from "types/target-type";
import { TbCoin } from "react-icons/tb";
import TooltippedElement from "components/TooltippedElement";
import { useLineItemContext } from "./context/LineItemContext";

const INLINE_ASSIGNEE_TYPES: TTargetType[] = [
  "appointment",
  "appointment-type",
  "appointment-type-default",
];

const isClickInsideReactSelectMenu = (target: Node): boolean => {
  if (!(target instanceof Element)) return false;
  const portal = document.getElementById("react-select");
  if (portal?.contains(target)) return true;
  return target.closest(".Select__menu, .Select__menu-portal") !== null;
};

interface EachProductProps extends TTargetProduct {
  targetType: TTargetType;
  lineItemsId: string;
  priceType?: "list" | "sale" | "install";
  rateLabel: "list_price" | "unit_price" | "install_price";
  hideEmpty: boolean;
}

const EachProduct: React.FC<EachProductProps> = (props) => {
  const { t } = useTranslation();
  const tBase = "containers.lineItems.eachProduct";
  const tr = (key: string) => t(`${tBase}.${key}`);

  const [isEditingAssignee, setIsEditingAssignee] = useState(false);
  const assigneePanelRef = useRef<HTMLDivElement>(null);

  const { setModal } = useModalContext();
  const { noTax } = useLineItemContext();

  const usesInlineAssignee = INLINE_ASSIGNEE_TYPES.includes(props.targetType);

  useEffect(() => {
    if (!isEditingAssignee || !usesInlineAssignee) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (assigneePanelRef.current?.contains(target)) return;
      if (isClickInsideReactSelectMenu(target)) return;
      setIsEditingAssignee(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isEditingAssignee, usesInlineAssignee]);

  if (!props.quantity && props.hideEmpty) return null;

  const handleClickProductionAssignee = () => {
    if (usesInlineAssignee) {
      setIsEditingAssignee((open) => !open);
      return;
    }

    const targetId = parseInt(props.id);
    setModal({
      label: tr("Assign Production"),
      component: (
        <ProductionAssigneeSelect
          targetId={targetId}
          fkUserId={props.fk_user_id}
          tech={props.tech}
          lineItemsId={props.lineItemsId}
          lineItemsType={props.targetType}
        />
      ),
    });
  };

  const handleCloseInlineAssignee = () => {
    setIsEditingAssignee(false);
  };

  return (
    <div className="py-2 px-0 flex items-center ml-2">
      <div style={{ flex: "1" }}>
        <div>
          <span>
            {props.quantity}&nbsp; &#10761; &nbsp;
            {props.label}
            <span> • </span>
            <span className="text-sm font-bold text-slate-500">
              {props.long_description}
            </span>
          </span>
          &nbsp; &nbsp;
          <span
            style={{
              flex: "1",
              color: theme.color.subtext3,
              fontSize: theme.fontSize.xsmall,
            }}
          >
            {`${tr("List")}: ${dollarFormatter(
              props.list_price
            )} | ${tr("Sale")}: ${dollarFormatter(
              props.unit_price
            )} | ${tr("Install")}: ${dollarFormatter(props.install_price)}`}
          </span>
        </div>
        <div className="flex items-center">
          <div className="text-xs text-secondary">
            {/* {dollarFormatter(props.production_value)} */}
          </div>
          <div
            ref={assigneePanelRef}
            className="relative flex ml-0 text-[12px] font-bold text-slate-500"
          >
            {`${tr("Added by")} ${props.tech}`}
            <button
              type="button"
              className="ml-2 text-[10px] text-primary hover:text-slate-400 focus:outline-none"
              onClick={handleClickProductionAssignee}
            >
              {tr("Edit")}
            </button>
            {usesInlineAssignee && isEditingAssignee && (
              <div
                className="absolute left-0 bottom-full mb-1 min-w-[280px] rounded border border-slate-200 bg-white p-3 shadow-lg"
                style={{ zIndex: theme.zIndex.tour }}
              >
                <ProductionAssigneeSelect
                  targetId={parseInt(props.id)}
                  fkUserId={props.fk_user_id}
                  tech={props.tech}
                  lineItemsId={props.lineItemsId}
                  lineItemsType={props.targetType}
                  onClose={handleCloseInlineAssignee}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <span className={`float-right relative ${props.taxable && !noTax ? " mr-[12.5px]" : "mr-none"} `}>
        {dollarFormatter(props.quantity * +(props[props.rateLabel] || 0))}
        {props.taxable && !noTax && (
          <div className="absolute right-[-12.5px] top-[-6px]">
            <TooltippedElement
              message="Taxed"
              placement="bottom"
              element={
                <TbCoin className="text-primary" />
              }
            />
          </div>
        )}
      </span>
    </div>
  );
};

export default EachProduct;
